import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import Konva from "konva";
import { Html } from "react-konva-utils";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useDismiss,
  useRole,
  useHover,
  useInteractions,
  arrow,
  Placement,
  FloatingArrow,
  useTransitionStyles,
} from "@floating-ui/react";
import styles from "./tooltip.module.css";
import { useKonvaVirtualElement } from "./use-konva-virtual-element";
import { useAtomValue } from "jotai";
import { stageRefAtom } from "state-atoms";

type KonvaTooltipProps = {
  text: React.ReactNode;
  nodeRef: React.RefObject<Konva.Node>;
  side?: Placement;
  offset?: number;
  maxWidth?: number;
  disabled?: boolean;
  open?: boolean;
};

/**
 * A tooltip component that works with Konva canvas elements.
 * Uses Floating UI for positioning but renders as a DOM element.
 *
 * @example
 * ```tsx
 * const nodeRef = useRef<Konva.Text | null>(null);
 *
 * <Text ref={nodeRef} />
 *
 * <KonvaTooltip
 *   text="Hover text"
 *   nodeRef={nodeRef}
 * />
 * ```
 */
export function KonvaTooltip({
  text,
  nodeRef,
  side = "top",
  maxWidth,
  disabled = false,
  offset: offsetValue = 8,
  open: controlledOpen,
}: KonvaTooltipProps) {
  const stageRef = useAtomValue(stageRefAtom);
  const stage = stageRef?.current ?? null;

  const [internalOpen, setInternalOpen] = useState(false);
  const open = useRef(false);

  const [konvaNode, setKonvaNode] = useState<Konva.Node | null>(null);
  const arrowRef = useRef(null);
  const isMouseDownRef = useRef(false);

  // Poll the ref until it's populated
  useEffect(() => {
    if (nodeRef?.current && !konvaNode) {
      setKonvaNode(nodeRef.current);
    }
  }, [nodeRef, konvaNode]);

  // Use controlled open if provided, otherwise use internal state
  open.current = controlledOpen === undefined ? internalOpen : controlledOpen;

  // Close the tooltip when the stage is moved or scaled
  useEffect(() => {
    if (controlledOpen !== undefined || !stage) return;

    const handleStageTransform = () => {
      if (open.current) {
        setInternalOpen(false);
      }
    };

    // Listen to stage transform events
    stage.on("dragmove", handleStageTransform);
    stage.on("wheel", handleStageTransform);
    stage.on("touchmove", handleStageTransform);

    return () => {
      stage.off("dragmove", handleStageTransform);
      stage.off("wheel", handleStageTransform);
      stage.off("touchmove", handleStageTransform);
    };
  }, [stage, controlledOpen]);

  // Create a virtual element that Floating UI can use for positioning
  const virtualElement = useKonvaVirtualElement(konvaNode, stage);

  const { refs, floatingStyles, context } = useFloating({
    open: open.current && !disabled && !!konvaNode,
    onOpenChange: controlledOpen === undefined ? setInternalOpen : undefined,
    placement: side,
    transform: false,
    middleware: [offset(offsetValue), flip(), shift({ padding: 8 }), arrow({ element: arrowRef })],
    whileElementsMounted: autoUpdate,
  });

  // Set the virtual element as the reference
  useEffect(() => {
    if (konvaNode && stage) {
      refs.setReference(virtualElement);
    }
  }, [refs, konvaNode, stage, virtualElement]);

  const hover = useHover(context, {
    delay: { open: 350, close: 150 },
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });

  const { getFloatingProps } = useInteractions([hover, dismiss, role]);

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: 200,
    initial: {
      opacity: 0,
      transform: "scale(0.8)",
    },
    common: ({ side }) => ({
      transformOrigin: {
        top: "bottom",
        bottom: "top",
        left: "right",
        right: "left",
      }[side],
    }),
  });

  // Handle Konva node events (only if not controlled)
  useEffect(() => {
    if (controlledOpen !== undefined) return; // Skip if controlled externally

    if (!konvaNode || disabled) {
      setInternalOpen(false);
      return;
    }

    const handleMouseEnter = () => {
      // Don't open tooltip if mouse is currently down
      if (!isMouseDownRef.current) {
        setInternalOpen(true);
      }
    };

    const handleMouseLeave = () => {
      setInternalOpen(false);
    };

    const handleMouseDown = () => {
      isMouseDownRef.current = true;
      // Close tooltip immediately when mouse is pressed
      setInternalOpen(false);
    };

    const handleMouseUp = () => {
      isMouseDownRef.current = false;
    };

    konvaNode.listening(true);
    konvaNode.on("mouseenter", handleMouseEnter);
    konvaNode.on("mouseleave", handleMouseLeave);
    konvaNode.on("mousedown", handleMouseDown);
    konvaNode.on("mouseup", handleMouseUp);

    return () => {
      konvaNode.off("mouseenter", handleMouseEnter);
      konvaNode.off("mouseleave", handleMouseLeave);
      konvaNode.off("mousedown", handleMouseDown);
      konvaNode.off("mouseup", handleMouseUp);
    };
  }, [konvaNode, disabled, controlledOpen]);

  // Handle stage-level drag events to close tooltip while dragging the node
  useEffect(() => {
    if (controlledOpen !== undefined || !stage || !konvaNode) return;

    const handleDragMove = (e: Konva.KonvaEventObject<DragEvent>) => {
      // Check if the dragged element is the tooltip node or a parent
      const draggedNode = e.target;
      let node: Konva.Node | null = konvaNode;
      while (node) {
        if (node === draggedNode) {
          // The dragged element is our node or a parent - close tooltip
          setInternalOpen(false);
          break;
        }
        node = node.getParent();
      }
    };

    stage.on("dragmove", handleDragMove);

    return () => {
      stage.off("dragmove", handleDragMove);
    };
  }, [stage, konvaNode, controlledOpen]);

  if (!konvaNode || !stage || disabled || !isMounted || !text) {
    return null;
  }

  const tooltipContent = (
    <div
      ref={refs.setFloating}
      style={{
        ...floatingStyles,
        ...transitionStyles,
        maxWidth: maxWidth ? `${maxWidth}px` : undefined,
        userSelect: "none",
      }}
      className={styles.tooltip}
      {...getFloatingProps()}
    >
      {text}
      <FloatingArrow ref={arrowRef} context={context} tipRadius={1.5} width={10} height={5} fill="#1E1E1E" />
    </div>
  );

  return <Html>{createPortal(tooltipContent, document.body)}</Html>;
}
